/* eslint-disable no-underscore-dangle */
import { useMemo } from 'react';
import _ from 'lodash';

import { useDataService } from './ApiDataService';
import { addPlmPrefix } from '../utilities/PgmUtilities';
import { buildUrlParamValue, parseUrlParamValue, filterStateParam } from '../utilities/FilterStateUrlUtilities';

const baseUrl = '/ipm/v1c/reports';
const scheduleUrl = '/ngp/v2/reports';
const reportSearchFields = 'name,user.userName,description,recurrence.interval,email.recipients';

export function isNewReport(report) {
    return report._id === undefined;
}

export function extractReportOptions(report) {
    return {
        ..._.omit(report, 'pdfSettings'),
        pdf: _.merge({}, report.pdfSettings, _.pick(report.page, 'maxTableRows')),
        page: _.omit(report.page, 'maxTableRows'),
        filters: {
            encodedState: parseUrlParamValue(report?.page?.urlParams?.filterState || ''),
        },
        user: report.user._id,
    };
}

export function buildReportOptions(options, preview = false) {
    // Convert the filter otions to the page URL param.
    if (options?.filters?.encodedState) {
        options.page.urlParams[filterStateParam] = buildUrlParamValue(options.filters.encodedState);
    }

    const reportOptions = _.merge(
        {
            page: {
                ...options.page,
                sessionStorage: {
                    ...options.page.sessionStorage,
                    printing: true,
                },
            },
        },
        {
            // The API expects the property to be `pdfSettings`, but I've shortened it to just `pdf` everywhere else.
            // This converts the options from the preferred format to the API's expected format.  This also omits the
            // `maxTableRows` property from the pdf settings, since it will cause the scheduled reporting API to fail
            // if it is present.
            pdfSettings: _.omit(options.pdf, 'maxTableRows'),
            email: options.email,
            recurrence: options.recurrence,
            name: options.name,
            user: options.user,
            organization: '00000000-0000-0000-0000-000000000000',
        },
        {
            page: {
                // The portal/ui app had maxTableRows living in the page settings, but the React form
                // only binds to pdf/pdfSettings. We need to keep using that setting as we'll be creating
                // reports in React, but managing those reports in portal/ui, so the original location needs
                // to be used (also for backwards compatibility). When we migrate to just React, it may be
                // worth changing this to live in the pdfSettings, though, it may logically still belong in
                // page, since it doesn't have a direct impact on PDFs like size/scale/orientation do.
                maxTableRows: options.pdf.maxTableRows,
            },
        }
    );

    // Only include the preview option if it's true, since we don't want this getting saved in a report
    // configuration.
    if (preview) {
        reportOptions.preview = preview;
    }

    if (options._id) {
        reportOptions._id = options._id;
    }

    return reportOptions;
}

export default function ReportServiceFactory(dataService) {
    function getPdfUrl(pdfResponse, preview = false) {
        const urlParams = preview ? '?preview=true' : '';
        return addPlmPrefix(`${baseUrl}/pdf/${encodeURIComponent(pdfResponse.id)}${urlParams}`, dataService.plmId);
    }

    function createPdf(options) {
        return dataService.post(`${baseUrl}/pdf`, options);
    }

    function emailPdf(options) {
        return dataService.post(`${baseUrl}/email`, options);
    }

    function schedulePdf(options) {
        if (isNewReport(options)) {
            return dataService.post(scheduleUrl, options);
        }

        return dataService.put(`${scheduleUrl}/${options._id}`, options);
    }

    function downloadPdf(options) {
        return createPdf(options).then((response) => {
            // The getPdfUrl function returns a PLM-prefixed URL, so using assignLocation here is unnecessary.
            // eslint-disable-next-line react-portal/no-window-location-assign
            window.location.assign(getPdfUrl(response));
            return response;
        });
    }

    function getReports(params) {
        return dataService.get(scheduleUrl, {
            ...params,
            expand: true,
            searchFields: reportSearchFields,
        });
    }

    function deleteReports(params, selection) {
        return dataService.remove(
            scheduleUrl,
            {
                selectionParams: selection,
            },
            {
                ...params,
                expand: true,
                searchFields: reportSearchFields,
            }
        );
    }

    function enableReports(params, selection) {
        return enableOrDisableReports(params, selection, false);
    }

    function disableReports(params, selection) {
        return enableOrDisableReports(params, selection, true);
    }

    function enableOrDisableReports(params, selection, disabled) {
        return dataService.patch(
            scheduleUrl,
            {
                selectionParams: selection,
                patchFields: {
                    disabled,
                },
            },
            {
                ...params,
                expand: true,
                searchFields: reportSearchFields,
            }
        );
    }

    return {
        createPdf,
        downloadPdf,
        emailPdf,
        schedulePdf,
        getPdfUrl,
        getReports,
        enableReports,
        disableReports,
        deleteReports,
    };
}

export function useReportService() {
    const dataService = useDataService();
    return useMemo(() => ReportServiceFactory(dataService), [dataService]);
}
